import { Family } from "protogen/advisors_service_pb";
import { FeedEntry, Medium } from "protogen/feed_pb";
import { AccountStub, Member } from "protogen/common_pb";
import { Box, FormHelperText, Menu, MenuItem, Typography } from "@mui/material";
import TextMessageCompose from "./TextMessageCompose";
import EmailComposeDialog from "../email/EmailComposeDialog";
import CallInstantiation from "./CallInstantiation";
import { FeedFocusState } from "../../types/feed";
import { Advisor, UserPermission } from "protogen/common_pb";
import { useContext, useRef, useState, useEffect } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { ChevronDown } from "lucide-react";
import { commaSeparatedEnglishList } from "../../common/utils";
import Autoreplies from "components/common/Autoreplies";
import { useSearchParams } from "react-router-dom";

// A deactivated/removed member keeps its row but its phone is tombstoned with a "__<hash>__"
// prefix, so a truthy primaryPhone is not enough to know we can actually text them.
const hasValidPhone = (m: Member): boolean =>
  !!m.primaryPhone && !m.primaryPhone.startsWith("__");

// A deactivated/removed member keeps its row but its phone is tombstoned with a "__<hash>__"
// prefix. We hide these from recipient selection entirely.
const isInactiveContact = (m: Member): boolean =>
  !!m.primaryPhone && m.primaryPhone.startsWith("__");

// Group threads can include external contacts, who have no Member row. Members expose a
// tombstoned phone when deactivated; external contacts only expose `canText`. Unresolvable refs
// are kept as unsendable rather than dropped, so a partial thread disables send instead of
// silently addressing a subset of the group.
type GroupRecipient = {
  ref: string;
  firstName: string;
  displayName: string;
  sendable: boolean;
  inactive: boolean;
};

const resolveRecipients = (
  family: Family,
  accountMap: Record<string, AccountStub>,
  refs: string[],
): GroupRecipient[] => {
  const memberByRef = new Map(family.familyMembers.map((m) => [m.ref, m]));
  return refs.map((ref) => {
    const member = memberByRef.get(ref);
    if (member) {
      return {
        ref,
        firstName: member.firstName,
        displayName: member.displayName,
        sendable: hasValidPhone(member),
        inactive: isInactiveContact(member),
      };
    }
    const stub = accountMap[ref];
    return {
      ref,
      firstName: stub?.firstName || stub?.displayName || "Unknown",
      displayName: stub?.displayName || stub?.firstName || "Unknown",
      sendable: !!stub?.canText,
      inactive: !stub,
    };
  });
};

type RecipientChange =
  | { groupText: false; selectedContactRef: string }
  | { groupText: true; groupRecipientRefs: string[] };

const ContactSelection = ({
  recipient,
  feedFocusState,
  setFeedFocusState,
  family,
  groupThreads,
  accountMap,
  onRecipientChange,
}: {
  recipient: Member;
  feedFocusState: FeedFocusState;
  setFeedFocusState: (s: FeedFocusState) => void;
  family: Family;
  groupThreads: string[][];
  accountMap: Record<string, AccountStub>;
  onRecipientChange?: (change: RecipientChange) => void;
}) => {
  const anchorEl = useRef<HTMLElement>();
  const [open, setOpen] = useState(false);
  const groupRecipients = resolveRecipients(
    family,
    accountMap,
    feedFocusState.groupRecipientRefs,
  );
  return (
    <>
      <Box
        ref={anchorEl}
        display="flex"
        flexDirection="row"
        padding="0px 16px"
        alignItems="center"
        gap="4px"
        sx={{
          cursor: "pointer",
          marginTop: "12px",
          marginBottom: "-8px",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Typography variant="bodySmall" sx={{ fontWeight: 600 }}>
          To{" "}
          {feedFocusState.groupText
            ? groupRecipients.map((g) => g.firstName).join(" & ")
            : recipient.firstName}
        </Typography>
        <ChevronDown size={18} />
      </Box>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          mt: "-40px", // Move the menu up
          ml: "16px", // Move the menu left
        }}
      >
        {family.familyMembers
          .filter((m) => !isInactiveContact(m))
          .map((m) => (
            <MenuItem
              key={m.ref}
              value={m.ref}
              disabled={!hasValidPhone(m)}
              onClick={() => {
                setFeedFocusState({
                  ...feedFocusState,
                  isDirty: true,
                  selectedContact: m,
                  replyToEmail: null,
                  editEmailDraft: null,
                  groupText: false,
                  groupRecipientRefs: [],
                });
                onRecipientChange?.({
                  groupText: false,
                  selectedContactRef: m.ref,
                });
                setOpen(false);
              }}
            >
              <Box display={"flex"} flexDirection={"column"}>
                <div>{m.displayName}</div>
                {!hasValidPhone(m) && (
                  <FormHelperText sx={{ mt: 0 }}>
                    Missing phone number
                  </FormHelperText>
                )}
              </Box>
            </MenuItem>
          ))}
        {groupThreads
          .map((refs) => ({
            refs,
            recipients: resolveRecipients(family, accountMap, refs),
          }))
          .filter(({ recipients }) => recipients.every((r) => !r.inactive))
          .map(({ refs, recipients }) => {
            const sendable = recipients.every((r) => r.sendable);
            return (
              <MenuItem
                key={refs.join("|")}
                value={refs.join("|")}
                disabled={!sendable}
                onClick={() => {
                  setFeedFocusState({
                    ...feedFocusState,
                    isDirty: true,
                    groupText: true,
                    groupRecipientRefs: refs,
                    replyToEmail: null,
                    editEmailDraft: null,
                  });
                  onRecipientChange?.({
                    groupText: true,
                    groupRecipientRefs: refs,
                  });
                  setOpen(false);
                }}
              >
                <Box display={"flex"} flexDirection={"column"}>
                  <div>{recipients.map((r) => r.firstName).join(" & ")}</div>
                  {!sendable && (
                    <FormHelperText sx={{ mt: 0 }}>
                      Missing phone number
                    </FormHelperText>
                  )}
                </Box>
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
};

type MessageComposeProps = {
  feedFocusState: FeedFocusState;
  setFeedFocusState: (s: FeedFocusState) => void;
  family: Family;
  groupThreads: string[][];
  accountMap: Record<string, AccountStub>;
  addFeedEntry: (e: FeedEntry) => void;
  removeFeedEntry: (ref: string, m: Medium) => void;
  primaryAdvisor: Advisor;
  advisorPermissions: UserPermission[];
  quickResponseSuggestions: string[];
  actionCallbacks?: {
    onTextMessageSent: (e: FeedEntry) => void;
    onEmailSent: (e: FeedEntry) => void;
    onPhoneCallStarted: (e: FeedEntry) => void;
  };
  setCachedRecipient: (
    c: {
      selectedContactRef: string;
      groupText: boolean;
      groupRecipientRefs: string[];
    } | null,
  ) => void;
};

export default ({
  feedFocusState,
  setFeedFocusState,
  family,
  groupThreads,
  accountMap,
  addFeedEntry,
  removeFeedEntry,
  primaryAdvisor,
  advisorPermissions,
  quickResponseSuggestions,
  actionCallbacks,
  setCachedRecipient,
}: MessageComposeProps) => {
  const [quickResponse, setQuickResponse] = useState<string>("");
  const [isQuickResponseSet, setIsQuickResponseSet] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const currentUser = useContext(CurrentUserContext);
  const isPhoneMedium = feedFocusState.selectedMedium === Medium.PHONE;
  const isEmailMedium = feedFocusState.selectedMedium === Medium.EMAIL;
  const recipient = feedFocusState.selectedContact;

  // Watch for textMessage param in URL and set quick response if present
  useEffect(() => {
    const textMessageParam = searchParams.get("textMessage");
    if (textMessageParam) {
      setQuickResponse(textMessageParam);
      setIsQuickResponseSet(true);
    }
  }, [searchParams]);

  const groupRecipients = resolveRecipients(
    family,
    accountMap,
    feedFocusState.groupRecipientRefs,
  );
  const isAdvisor = primaryAdvisor.ref === currentUser.ref;
  const delegate = advisorPermissions.find(
    (p) => p.account?.ref === currentUser.ref && p.active,
  );
  const communicationEnabled = isAdvisor || !!delegate?.actions?.communicate;
  const onClose = () => {
    setFeedFocusState({
      ...feedFocusState,
      replyToEmail: null,
      editEmailDraft: null,
      selectedMedium: Medium.SMS,
    });
  };
  const initialCC = family.familyMembers
    .filter((m) => m.ref !== recipient.ref && m.primaryEmail)
    .map((m) => m.primaryEmail);
  if (delegate && communicationEnabled) {
    initialCC.push(primaryAdvisor.username);
  }
  let placeholder = `Text ${recipient.firstName}`;
  if (feedFocusState.groupText) {
    placeholder = `Text ${commaSeparatedEnglishList(
      groupRecipients.map((r) => r.firstName),
    )}`;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Contact selection */}
      <ContactSelection
        feedFocusState={feedFocusState}
        recipient={recipient}
        setFeedFocusState={setFeedFocusState}
        family={family}
        groupThreads={groupThreads}
        accountMap={accountMap}
        onRecipientChange={(change) => {
          setCachedRecipient(
            change.groupText
              ? {
                  selectedContactRef: recipient.ref,
                  groupText: true,
                  groupRecipientRefs: change.groupRecipientRefs,
                }
              : {
                  selectedContactRef: change.selectedContactRef,
                  groupText: false,
                  groupRecipientRefs: [],
                },
          );
        }}
      />
      {!isQuickResponseSet && (
        <Autoreplies
          suggestions={quickResponseSuggestions}
          onSelect={(text) => {
            setQuickResponse(text);
            setIsQuickResponseSet(true);
          }}
        />
      )}
      <TextMessageCompose
        key={quickResponse}
        forceDisabled={!communicationEnabled}
        familyRef={family.ref}
        recipientRefs={
          feedFocusState.groupText
            ? feedFocusState.groupRecipientRefs
            : [recipient.ref]
        }
        contactEnabled={
          feedFocusState.groupText
            ? groupRecipients.length > 0 &&
              groupRecipients.every((r) => r.sendable)
            : hasValidPhone(recipient)
        }
        placeholder={placeholder}
        onSent={(e) => {
          addFeedEntry(e);
          setIsQuickResponseSet(false);
          setCachedRecipient(null);
          actionCallbacks?.onTextMessageSent?.(e);
        }}
        initialContent={quickResponse}
        onUpdateContent={() => {
          setIsQuickResponseSet(true);
        }}
      />
      <EmailComposeDialog
        closed={!isEmailMedium || !communicationEnabled}
        onClose={onClose}
        onSent={(e) => {
          addFeedEntry(e);
          actionCallbacks?.onEmailSent?.(e);
        }}
        onRemove={removeFeedEntry}
        inReplyTo={feedFocusState.replyToEmail}
        initialDraft={feedFocusState.editEmailDraft}
        initialTo={[recipient.primaryEmail]}
        initialCC={initialCC}
      />
      <CallInstantiation
        closed={!isPhoneMedium || !communicationEnabled}
        onClose={onClose}
        family={family}
        recipientRef={recipient.ref}
        onStarted={(e) => {
          addFeedEntry(e);
          actionCallbacks?.onPhoneCallStarted?.(e);
        }}
      />
    </Box>
  );
};
